"use client";

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { Heading } from "@/components/ui/typography";
import { useSelfServeSettings } from "@/hooks/use-self-serve-settings";
import { AlertCircle, Mail } from "lucide-react";

export default function SelfServeTenantsPage() {
  const t = useTranslations("tenants.selfServe");
  const { isSelfServeOpen, loading } = useSelfServeSettings();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = pathname?.split("/")[1] || "zh-CN";

  const isDev = process.env.NODE_ENV !== "production";
  const h1w = searchParams?.get("h1w");
  const h1WeightClass = useMemo(() => {
    if (!isDev) return undefined;
    switch ((h1w || "").toLowerCase()) {
      case "bold":
        return "font-bold";
      case "semibold":
        return "font-semibold";
      case "medium":
        return "font-medium";
      default:
        return undefined;
    }
  }, [h1w, isDev]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await fetch("/api/tenants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          description: description || undefined,
          domain: domain || undefined,
          locale,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const code =
          payload?.error === "Tenant creation limit reached"
            ? "limit"
            : "create_failed";
        throw new Error(code);
      }

      const approvalStatus = String(
        payload?.tenant?.approvalStatus || "",
      ).toUpperCase();
      const isApproved = approvalStatus === "APPROVED";

      setName("");
      setDescription("");
      setDomain("");
      setMessage(
        isApproved
          ? t("messages.created")
          : t("messages.submitted", {
              default: t("messages.created", {
                default: "注册已提交，等待审核。",
              }),
            }),
      );

      if (isApproved) {
        setTimeout(() => {
          router.push(`/${locale}/organizations?onboarding=1`);
        }, 1200);
      }
    } catch (error: any) {
      const code = error?.message === "limit" ? "limit" : "failed";
      setMessage(
        t(`messages.${code}`, {
          default: t("messages.failedFallback", {
            default: "创建失败，请稍后再试。",
          }),
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="rounded-2xl border border-rose-100/80 bg-white/90 shadow-sm backdrop-blur-sm p-6 space-y-6 dark:border-rose-900/30 dark:bg-slate-900/60">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </div>
        ) : !isSelfServeOpen ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <Heading
                  variant="h1"
                  as="h1"
                  className={`text-foreground font-semibold ${h1WeightClass ?? ""}`}
                >
                  {t("selfServeClosed.title", {
                    default: "Self-Serve Tenant Creation Disabled",
                  })}
                </Heading>
                <p className="text-sm text-muted-foreground">
                  {t("selfServeClosed.description", {
                    default: "Tenant self-registration is currently disabled. Please contact your administrator to create a tenant for your organization.",
                  })}
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-amber-800 mb-3">
                    {t("selfServeClosed.contactHint", {
                      default: "To request a tenant creation, please contact our support team with your organization details.",
                    })}
                  </p>
                  <Link
                    href={`/${locale}/contact?subject=support`}
                    className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-100 px-4 py-2 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100 dark:hover:bg-amber-900/50"
                  >
                    {t("selfServeClosed.contactButton", {
                      default: "Contact Support",
                    })}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Heading
                variant="h1"
                as="h1"
                className={`text-foreground font-semibold ${h1WeightClass ?? ""}`}
              >
                {t("title")}
              </Heading>
              <p className="text-sm text-muted-foreground">{t("subtitle")}</p>
            </div>

            {message && (
              <div className="mb-2 rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
                {message}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  {t("form.name.label")}
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("form.name.placeholder")}
                  className="w-full rounded-md border border-rose-100 bg-background px-3 py-2 outline-none focus:border-rose-200 focus:ring-2 focus:ring-rose-200 dark:border-rose-900/40 dark:focus:border-rose-700/60 dark:focus:ring-rose-700/30"
                  required
                  onInvalid={(e) => {
                    (e.currentTarget as HTMLInputElement).setCustomValidity(
                      t("form.name.required", {
                        default: "Please fill out this field.",
                      }),
                    );
                  }}
                  onInput={(e) => {
                    (e.currentTarget as HTMLInputElement).setCustomValidity("");
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  {t("form.description.label")}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-md border border-rose-100 bg-background px-3 py-2 outline-none focus:border-rose-200 focus:ring-2 focus:ring-rose-200 dark:border-rose-900/40 dark:focus:border-rose-700/60 dark:focus:ring-rose-700/30"
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  {t("form.domain.label")}
                </label>
                <input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder={t("form.domain.placeholder", {
                    default: "e.g. yourschool.talkmaster.com",
                  })}
                  className="w-full rounded-md border border-rose-100 bg-background px-3 py-2 outline-none focus:border-rose-200 focus:ring-2 focus:ring-rose-200 dark:border-rose-900/40 dark:focus:border-rose-700/60 dark:focus:ring-rose-700/30"
                />
                <p className="text-xs text-muted-foreground">
                  {t("form.domain.help", {
                    default:
                      "You can use a subdomain or bind a custom domain later.",
                  })}
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center rounded-lg border border-rose-200 bg-rose-400/90 px-4 py-2 text-white shadow-sm transition-colors hover:bg-rose-400 disabled:opacity-50 dark:border-rose-900/40"
              >
                {t("form.submit")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
